import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router } from 'react-router-dom';
import Header from './components/Header';
import Routes from './routes';
import SeriesPopupPreviewOverlay from './components/SeriesPopupPreviewOverlay';
import Bottom from './components/Bottom';
import Loader from './Loader';
import { AuthProvider } from './contexts/AuthContext';
import { useLocation } from 'react-router-dom';
import './styles/globalStyles';
import styled from 'styled-components';

const PageLoader = () => {
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const shownOnceRef = React.useRef(false);

  useEffect(() => {
    function onRemapped(ev) {
      try {
        const detail = ev && ev.detail ? ev.detail : {};
        const id = detail && detail.id ? String(detail.id) : null;
        if (!id) return;
        // Fetch remaps and apply for current user (reuse logic above)
        (async () => {
          try {
            const r = await fetch(`/api/popups/${encodeURIComponent(id)}/remaps`, { cache: 'no-store' });
            if (!r.ok) return;
            const j = await r.json();
            const remaps = j && j.remaps ? j.remaps : {};
            if (!remaps || Object.keys(remaps).length === 0) return;
            const contApi = await import('./api/continue');
            const uid = window.__CURRENT_USER_ID__ || null;
            if (!uid) return;
            const list = await contApi.listSeries(uid, 200);
            const matches = (list || []).filter(c => c && String(c.popupId) === String(id) && c.playId && remaps[c.playId]);
            for (const m of matches) {
              const newPlay = remaps[m.playId];
              try { await contApi.upsertSeries(uid, String(id), { ...m, playId: newPlay, path: `/play/${newPlay}` }); } catch (_) {}
            }
          } catch (_) {}
        })();
      } catch (_) {}
    }
    window.addEventListener('popup:remapped', onRemapped);
    return () => window.removeEventListener('popup:remapped', onRemapped);
  }, []);

  useEffect(() => {
    // Only show the loader on the very first app load, not on each route change
    if (shownOnceRef.current) return;
    let alive = true;
    (async () => {
      setLoading(true);
      await new Promise((r) => setTimeout(r, 250));
      if (!alive) return;
      setLoading(false);
      shownOnceRef.current = true;
    })();
    return () => { alive = false; };
  }, [location.pathname]);

  if (!shownOnceRef.current && loading) return <Loader />;
  return null;
};

function HeaderGate() {
  const location = useLocation();
  const path = location.pathname || '';
  // Hide header entirely on play page
  if (path.startsWith('/play')) return null;
  return <Header />;
}

export default function App() {
  return (
      <AuthProvider>
      <Router>
      <PageLoader />
      <Shell>
        <HeaderGate />
        <PreviewOnLoad />
        <Content>
          <Routes />
          <PreviewLinkInterceptor />
          <SeriesPopupPreviewOverlay />
        </Content>
        <Bottom />
      </Shell>
    </Router>
    </AuthProvider>
  );
} 

function PreviewLinkInterceptor() {
  useEffect(() => {
    function onClick(e) {
      try {
        if (e.defaultPrevented) return;
        if (e.button !== 0) return; // only left click
        if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return; // respect modifiers
        let el = e.target;
        while (el && el.tagName !== 'A') el = el.parentElement;
        if (!el) return;
        // Allow specific links to opt-out of the preview interceptor by adding this attribute
        if (el.closest('[data-skip-preview-interceptor]')) return;
        const target = el.getAttribute('target');
        if (target && target !== '' && target !== '_self') return;
        const href = el.getAttribute('href');
        if (!href) return;
        const url = new URL(href, window.location.href);
        if (url.origin !== window.location.origin) return;
        // ignore same-path/hash updates
        if (url.pathname === window.location.pathname && url.search === window.location.search && url.hash) return;
        if (!url.searchParams.has('preview')) return;
  e.preventDefault();
  // Stop React Router Link onClick from firing
  try { if (typeof e.stopImmediatePropagation === 'function') e.stopImmediatePropagation(); } catch {}
  e.stopPropagation();
        // Update URL without notifying Router; dispatch open event for overlay
        window.history.pushState({}, '', url.pathname + url.search);
        const id = url.searchParams.get('preview');
        window.dispatchEvent(new CustomEvent('preview:open', { detail: { id } }));
      } catch {}
    }
    document.addEventListener('click', onClick, true);
    return () => document.removeEventListener('click', onClick, true);
  }, []);
  return null;
}

function PreviewOnLoad() {
  const location = useLocation();
  useEffect(() => {
    try {
      const url = new URL(window.location.href);
      let id = url.searchParams.get('preview');
      const sid = sessionStorage.getItem('openPreviewId');
      if (id) {
        // If preview is already in URL, ensure one-shot flag is cleared so it won't leak into /play later.
        if (sid) { try { sessionStorage.removeItem('openPreviewId'); } catch {} }
      } else if (sid) {
        // Only apply the preview flag on non-/play routes to avoid opening overlay on play page
        const pathNow = window.location.pathname || '/';
        if (!pathNow.startsWith('/play')) {
          id = sid;
          try { sessionStorage.removeItem('openPreviewId'); } catch {}
          const next = `${pathNow}?preview=${encodeURIComponent(id)}`;
          window.history.replaceState({}, '', next);
        } else {
          // We're on /play while a stale sid exists — clear it and do not open overlay here
          try { sessionStorage.removeItem('openPreviewId'); } catch {}
        }
      }
      if (id) {
        // Defer dispatch to ensure listeners in overlay are mounted
        setTimeout(() => {
          try { window.dispatchEvent(new CustomEvent('preview:open', { detail: { id } })); } catch {}
        }, 0);
        // Fire a second time shortly after as a safety net on cold loads
        setTimeout(() => {
          try { window.dispatchEvent(new CustomEvent('preview:open', { detail: { id } })); } catch {}
        }, 120);
        // If overlay mounts later and misses the early dispatch, also listen for preview:ready and re-open
        function onReady() {
          try { window.dispatchEvent(new CustomEvent('preview:open', { detail: { id } })); } catch {}
        }
        window.addEventListener('preview:ready', onReady);
        // remove listener shortly after to avoid permanent listeners
        setTimeout(() => { try { window.removeEventListener('preview:ready', onReady); } catch {} }, 3000);
        // As an extra safety net, dispatch again when the window regains focus or becomes visible
        function tryOpenOnFocus() {
          try {
            const u = new URL(window.location.href);
            const iid = u.searchParams.get('preview');
            if (iid) {
              try { window.dispatchEvent(new CustomEvent('preview:open', { detail: { id: iid } })); } catch {}
            }
          } catch {}
        }
        window.addEventListener('focus', tryOpenOnFocus);
        function onVisibilityChange() { if (document.visibilityState === 'visible') tryOpenOnFocus(); }
        document.addEventListener('visibilitychange', onVisibilityChange);
        // remove listeners after a short grace period to avoid leaking
        setTimeout(() => {
          try { window.removeEventListener('focus', tryOpenOnFocus); } catch {}
          try { document.removeEventListener('visibilitychange', onVisibilityChange); } catch {};
        }, 5000);
      }
      // After attempting to open preview, ask server if there are remaps for this popup and apply locally
      (async () => {
        try {
          const url = new URL(window.location.href);
          const id = url.searchParams.get('preview');
          if (!id) return;
          const r = await fetch(`/api/popups/${encodeURIComponent(id)}/remaps`, { cache: 'no-store' });
          if (!r.ok) return;
          const j = await r.json();
          const remaps = j && j.remaps ? j.remaps : {};
          if (!remaps || Object.keys(remaps).length === 0) return;
          // try to update current user's continueSeries entries if they reference oldPlayId
          try {
            // lazy-import contApi to avoid cycle
            const contApi = await import('./api/continue');
            // current user id read from window.__CURRENT_USER_ID__ if App sets it; fallback to null
            const uid = window.__CURRENT_USER_ID__ || null;
            if (!uid) return;
            const list = await contApi.listSeries(uid, 200);
            const matches = (list || []).filter(c => c && String(c.popupId) === String(id) && c.playId && remaps[c.playId]);
            for (const m of matches) {
              const newPlay = remaps[m.playId];
              try {
                await contApi.upsertSeries(uid, String(id), { ...m, playId: newPlay, path: `/play/${newPlay}` });
              } catch (_) {}
            }
          } catch (_) {}
        } catch (_) {}
      })();
    } catch {}
  }, [location.pathname, location.search]);
  return null;
}

// Layout wrappers to keep footer at the bottom on short pages
const Shell = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
`;

const Content = styled.div`
  flex: 1 0 auto;
  display: flex;
  flex-direction: column;
`;